// Inline review card shown on Product/Service detail pages.
//
// Responsibilities:
//  - Fetch /api/products/:id/reviews (public approved-only) + summary
//  - Fetch caller's own review (any status) so they can edit
//  - Render summary + collapsed list with a "View All" toggle
//  - Render a "Write a review" form (1-5 stars + text) that posts to
//    /api/products/:id/reviews. Submissions land in admin moderation.
//
// "View All" expands the existing card inline (no route change) per spec.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../config/axios';

function Stars({ value, onChange, size = 'md', testId }) {
  const sizes = { sm: 'text-sm', md: 'text-xl', lg: 'text-3xl' };
  return (
    <div className={`flex items-center gap-1 ${sizes[size]}`} data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(value);
        const interactive = !!onChange;
        return (
          <button
            type="button"
            key={n}
            onClick={interactive ? () => onChange(n) : undefined}
            disabled={!interactive}
            data-testid={interactive ? `${testId}-star-${n}` : undefined}
            className={`${interactive ? 'cursor-pointer' : 'cursor-default'} leading-none transition-transform ${interactive ? 'hover:scale-110' : ''}`}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
          >
            <i className={`${filled ? 'ph-fill ph-star text-[#F4B400]' : 'ph ph-star text-gray-300'}`} />
          </button>
        );
      })}
    </div>
  );
}

function ReviewItem({ review }) {
  const initials = (review.user_name || 'B').slice(0, 2).toUpperCase();
  const dateStr = review.created_at
    ? new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  return (
    <div className="border-t border-gray-100 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0" data-testid={`review-item-${review.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#2B3A67] text-white text-xs font-extrabold flex items-center justify-center shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-sm truncate">{review.user_name || 'Backyard user'}</p>
            {review.verified_buyer && (
              <span
                data-testid="review-verified-badge"
                className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"
              >
                <i className="ph-bold ph-seal-check" />
                Verified buyer
              </span>
            )}
            <span className="text-xs text-gray-400 ml-auto">{dateStr}</span>
          </div>
          <Stars value={review.rating} size="sm" testId={`review-item-${review.id}-stars`} />
          {review.text && (
            <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap break-words">
              {review.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function MyReviewForm({ productId, existing, onSaved }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [text, setText] = useState(existing?.text || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!rating) {
      toast.error('Pick a star rating first.');
      return;
    }
    try {
      setSaving(true);
      const res = await axiosInstance.post(`/products/${productId}/reviews`, {
        rating,
        text: text.trim(),
      });
      toast.success('Thanks! Your review is pending moderation.');
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 mt-4" data-testid="review-form">
      <p className="text-sm font-extrabold text-[#2B3A67] mb-2">
        {existing ? 'Update your review' : 'Write a review'}
      </p>
      {existing && (
        <p
          className="text-[11px] font-bold uppercase tracking-wide mb-2"
          data-testid="review-form-status"
        >
          Current status:{' '}
          <span className={
            existing.status === 'approved' ? 'text-emerald-600'
              : existing.status === 'rejected' ? 'text-red-500'
                : 'text-amber-600'
          }>
            {existing.status}
          </span>
        </p>
      )}
      <Stars value={rating} onChange={setRating} testId="review-form-rating" />
      <textarea
        data-testid="review-form-text"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={1000}
        placeholder="Share what you liked (optional)…"
        className="w-full mt-3 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2B3A67]/30"
      />
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        data-testid="review-form-submit"
        className="w-full mt-3 py-2.5 rounded-xl bg-[#2B3A67] text-white font-extrabold text-sm disabled:opacity-60"
      >
        {saving ? 'Submitting…' : existing ? 'Update review' : 'Submit review'}
      </button>
    </div>
  );
}

export default function ProductReviews({ productId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const pub = await axiosInstance.get(`/products/${productId}/reviews`);
      let mine = null;
      if (token) {
        try {
          const mineRes = await axiosInstance.get(`/products/${productId}/reviews/me`);
          mine = mineRes.data;  // may be null
        } catch { /* anonymous */ }
      }
      setData({ ...pub.data, my_review: mine });
    } catch {
      setData({ summary: { average: 0, count: 0, distribution: {} }, items: [], my_review: null });
    } finally {
      setLoading(false);
    }
  }, [productId, token]);

  useEffect(() => { load(); }, [load]);

  const visibleItems = useMemo(() => {
    if (!data?.items) return [];
    return expanded ? data.items : data.items.slice(0, 3);
  }, [data, expanded]);

  if (loading) {
    return (
      <section className="mt-6 bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="product-reviews-loading">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-100 rounded w-1/3" />
          <div className="h-3 bg-gray-100 rounded w-1/4 mt-2" />
        </div>
      </section>
    );
  }

  const { summary, items, my_review } = data || {};
  const hasMore = (items?.length || 0) > 3;

  return (
    <section
      className="mt-6 bg-white rounded-2xl p-5 shadow-md border border-gray-100"
      data-testid="product-reviews-card"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-extrabold">Reviews</h3>
          <div className="flex items-center gap-2 mt-1">
            <Stars value={summary.average} size="sm" testId="product-reviews-summary-stars" />
            <p className="text-xs text-gray-500" data-testid="product-reviews-summary-text">
              {summary.count > 0
                ? `${summary.average} average (${summary.count} review${summary.count === 1 ? '' : 's'})`
                : 'No reviews yet — be the first.'}
            </p>
          </div>
        </div>
        {(items?.length || 0) > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="text-xs font-bold text-[#E54562]"
            data-testid="product-reviews-toggle"
          >
            {expanded ? 'Collapse' : hasMore ? `View All (${items.length})` : 'View All'}
          </button>
        )}
      </div>

      {items?.length > 0 && (
        <div className="mt-4" data-testid="product-reviews-list">
          {visibleItems.map((r) => <ReviewItem key={r.id} review={r} />)}
        </div>
      )}

      {token && (
        <MyReviewForm
          productId={productId}
          existing={my_review}
          onSaved={() => load()}
        />
      )}
      {!token && (
        <p className="text-xs text-gray-400 mt-4" data-testid="product-reviews-login-cta">
          Sign in to write a review.
        </p>
      )}
    </section>
  );
}
